/**
 * Local-only persistence for Line learning signals. This module deliberately
 * has no Firebase imports: Phase 1 must not put telemetry on the network.
 */
export const LINE_SIGNALS_DB_NAME = 'line.signals.v1'
const DB_VERSION = 1
const STORE_NAME = 'signals'
const MAX_SIGNALS = 500

export type CoarsenedPlanType = 'trip' | 'event' | 'project' | 'personal' | 'health' | 'other'
export type CoarsenedWorkspaceType = 'personal' | 'team' | 'org' | 'public-demo'
export type AttachmentDensity = '0' | '1-3' | '4-10' | '10+'
export type TimelineSpan = 'day' | 'week' | 'month' | 'quarter' | 'longer'
export type BinnedCount = '0' | '1-10' | '11-40' | '40+'

export interface CoarsenedContextAxes {
  planType: CoarsenedPlanType
  workspaceType: CoarsenedWorkspaceType
  attachmentDensity: AttachmentDensity
  timelineSpan: TimelineSpan
  language: string
  changeSomethingRounds: 0 | 1 | 2 | 3 | '4+'
  attachmentExtractedCount: BinnedCount
  attachmentKeptCount: BinnedCount
}

export type StoredLineSignalKind =
  | 'PROPOSAL_BUILT'
  | 'PROPOSAL_REVISED'
  | 'PROPOSAL_DISCARDED'
  | 'LAGGING_USAGE_POSITIVE'
  | 'LAGGING_USAGE_NEGATIVE'
  | 'INTERVIEW_ESCALATION_FIRED'
  // This Plan 1 hook only records coarsened inventory counts. The future
  // aggregator can ignore this compatibility event when calculating scores.
  | 'ATTACHMENT_INVENTORY_CURATED'

export interface RawLineSignal {
  id: string
  kind: StoredLineSignalKind
  turnId: string
  emittedAt: number
  axes: CoarsenedContextAxes
  flushed: boolean
  attempt: number
}

let databasePromise: Promise<IDBDatabase> | null = null

const supportsIndexedDb = () => typeof indexedDB !== 'undefined'

const openDatabase = (): Promise<IDBDatabase> => {
  if (!supportsIndexedDb()) return Promise.reject(new Error('IndexedDB is unavailable.'))
  if (databasePromise) return databasePromise
  databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(LINE_SIGNALS_DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('emittedAt', 'emittedAt', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => {
      databasePromise = null
      reject(request.error ?? new Error('Could not open the Line signals store.'))
    }
  })
  return databasePromise
}

const transactionComplete = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.oncomplete = () => resolve()
  transaction.onerror = () => reject(transaction.error ?? new Error('Line signal storage transaction failed.'))
  transaction.onabort = () => reject(transaction.error ?? new Error('Line signal storage transaction aborted.'))
})

export const isCoarsenedContextAxes = (value: unknown): value is CoarsenedContextAxes => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const axes = value as Record<string, unknown>
  const expectedKeys = [
    'planType', 'workspaceType', 'attachmentDensity', 'timelineSpan', 'language',
    'changeSomethingRounds', 'attachmentExtractedCount', 'attachmentKeptCount',
  ]
  if (Object.keys(axes).length !== expectedKeys.length || Object.keys(axes).some((key) => !expectedKeys.includes(key))) return false
  return (
    ['trip', 'event', 'project', 'personal', 'health', 'other'].includes(String(axes.planType))
    && ['personal', 'team', 'org', 'public-demo'].includes(String(axes.workspaceType))
    && ['0', '1-3', '4-10', '10+'].includes(String(axes.attachmentDensity))
    && ['day', 'week', 'month', 'quarter', 'longer'].includes(String(axes.timelineSpan))
    && typeof axes.language === 'string'
    && /^[a-z]{2,3}$/.test(axes.language)
    && [0, 1, 2, 3, '4+'].includes(axes.changeSomethingRounds as string | number)
    && ['0', '1-10', '11-40', '40+'].includes(String(axes.attachmentExtractedCount))
    && ['0', '1-10', '11-40', '40+'].includes(String(axes.attachmentKeptCount))
  )
}

const trimOldestSignals = (store: IDBObjectStore) => {
  const countRequest = store.count()
  countRequest.onsuccess = () => {
    let remainingToDelete = Math.max(0, countRequest.result - MAX_SIGNALS)
    if (remainingToDelete === 0) return
    const cursorRequest = store.index('emittedAt').openCursor()
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result
      if (!cursor || remainingToDelete === 0) return
      cursor.delete()
      remainingToDelete -= 1
      cursor.continue()
    }
  }
}

/** Pure counterpart to the store eviction path, kept testable without an IDB shim. */
export const retainNewestLineSignals = (signals: RawLineSignal[]): RawLineSignal[] =>
  [...signals]
    .sort((left, right) => left.emittedAt - right.emittedAt)
    .slice(-MAX_SIGNALS)

/** Stores one safe, coarsened record and evicts the oldest overflow records. */
export const pushLineSignal = async (signal: RawLineSignal): Promise<boolean> => {
  if (!isCoarsenedContextAxes(signal.axes)) return false
  try {
    const database = await openDatabase()
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    store.put(signal)
    trimOldestSignals(store)
    await transactionComplete(transaction)
    return true
  } catch {
    return false
  }
}

export const listLineSignals = async (): Promise<RawLineSignal[]> => {
  try {
    const database = await openDatabase()
    return await new Promise<RawLineSignal[]>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readonly')
      const request = transaction.objectStore(STORE_NAME).getAll() as IDBRequest<RawLineSignal[]>
      request.onsuccess = () => resolve([...request.result].sort((left, right) => left.emittedAt - right.emittedAt))
      request.onerror = () => reject(request.error ?? new Error('Could not list Line signals.'))
    })
  } catch {
    return []
  }
}

export const clearLineSignals = async (): Promise<void> => {
  try {
    const database = await openDatabase()
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).clear()
    await transactionComplete(transaction)
  } catch {
    // Tests and callers may clean up in environments without IndexedDB.
  }
}

const updateStoredSignals = async (
  ids: readonly string[],
  update: (signal: RawLineSignal) => RawLineSignal | null,
): Promise<void> => {
  if (ids.length === 0) return
  try {
    const database = await openDatabase()
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    const store = transaction.objectStore(STORE_NAME)
    ids.forEach((id) => {
      const request = store.get(id) as IDBRequest<RawLineSignal | undefined>
      request.onsuccess = () => {
        const current = request.result
        if (!current) return
        const next = update(current)
        if (next) store.put(next)
        else store.delete(id)
      }
    })
    await transactionComplete(transaction)
  } catch {
    // A later idle window can retry records that could not be updated locally.
  }
}

export const markLineSignalsFlushed = (ids: readonly string[]) =>
  updateStoredSignals(ids, (signal) => ({ ...signal, flushed: true }))

/** Increments failed flush attempts and removes a record only after its third failed write. */
export const recordLineSignalFlushFailure = (ids: readonly string[]) =>
  updateStoredSignals(ids, (signal) => {
    const attempt = signal.attempt + 1
    return attempt >= 3 ? null : { ...signal, attempt }
  })

export const lineSignalStoreInternals = {
  clearLineSignals,
  isCoarsenedContextAxes,
  listLineSignals,
  markLineSignalsFlushed,
  pushLineSignal,
  recordLineSignalFlushFailure,
  retainNewestLineSignals,
}
